import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { CalendarIcon, CheckCircle, Users, AlertTriangle } from 'lucide-react';
import PublicLayout from '../../components/shared/PublicLayout';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';

const API = getApiBase();

export default function QuotePage() {
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({ guests: 10, event_address: '', email: '', phone: '', notes: '', vegetarian: false, normal: true, allergies: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (settings?.quotes_enabled === false) {
      toast.error('Les demandes de devis sont temporairement indisponibles');
      return;
    }
    if (!date || !form.email || !form.phone || !form.event_address) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (form.guests < 10) {
      toast.error('Minimum 10 personnes pour un devis');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/quotes`, {
        ...form,
        guests: parseInt(form.guests),
        event_date: format(date, 'yyyy-MM-dd'),
      });
      setSubmitted(true);
      toast.success('Demande de devis envoyee !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <CheckCircle className="w-16 h-16 text-[#5B6B3F] mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-[#2A2A28] mb-4">Demande envoyee !</h1>
          <p className="text-[#7A7A75] mb-8">Nous avons bien recu votre demande de devis. Nous reviendrons vers vous rapidement.</p>
          <Button onClick={() => { setSubmitted(false); setForm({ guests: 10, event_address: '', email: '', phone: '', notes: '', vegetarian: false, normal: true, allergies: '' }); setDate(null); }} className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="new-quote-btn">
            Nouvelle demande
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-medium mb-3">Evenements & Traiteur</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2A2A28] tracking-tighter">Demander un devis</h1>
          <p className="text-[#7A7A75] mt-3">Pour vos evenements de 10 personnes et plus</p>
        </div>

        {settings?.quotes_enabled === false && (
          <div className="mb-6 rounded-2xl border border-[#8C3A3A]/20 bg-[#8C3A3A]/8 px-4 py-3 text-sm text-[#8C3A3A]">
            Les demandes de devis sont temporairement indisponibles. Merci de revenir plus tard.
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#EBE8E0] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="quote-form">
          <div className="space-y-5">
            {/* Guests */}
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Nombre de personnes * (min. 10)</Label>
              <Input type="number" min={10} className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.guests} onChange={e => setForm({...form, guests: e.target.value})} required data-testid="quote-guests" />
              {form.guests < 10 && <p className="text-xs text-[#8C3A3A] mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Minimum 10 personnes</p>}
            </div>

            {/* Event Date */}
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> Date de l'evenement *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start mt-1.5 rounded-xl border-[#EBE8E0] text-left font-normal" data-testid="quote-date">
                    {date ? format(date, 'dd/MM/yyyy') : 'Choisir une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                </PopoverContent>
              </Popover>
            </div>

            {/* Event Address */}
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm">Adresse de l'evenement *</Label>
              <Input className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.event_address} onChange={e => setForm({...form, event_address: e.target.value})} placeholder="Adresse complete" required data-testid="quote-address" />
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#2A2A28] font-medium text-sm">Email *</Label>
                <Input type="email" className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="quote-email" />
              </div>
              <div>
                <Label className="text-[#2A2A28] font-medium text-sm">Telephone *</Label>
                <Input className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="quote-phone" />
              </div>
            </div>

            {/* Preferences */}
            <div className="bg-[#FAF8F5] rounded-xl p-4 border border-[#EBE8E0]">
              <Label className="text-[#2A2A28] font-medium text-sm mb-3 block">Type de repas</Label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.normal} onCheckedChange={v => setForm({...form, normal: v})} data-testid="quote-normal" />
                  <span className="text-sm text-[#2A2A28]">Menu classique</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.vegetarian} onCheckedChange={v => setForm({...form, vegetarian: v})} data-testid="quote-vegetarian" />
                  <span className="text-sm text-[#2A2A28]">Menu vegetarien</span>
                </label>
              </div>
            </div>

            {/* Allergies */}
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm">Allergies ou intolerances</Label>
              <Input className="mt-1.5 rounded-xl border-[#EBE8E0]" value={form.allergies} onChange={e => setForm({...form, allergies: e.target.value})} placeholder="Ex: gluten, arachides, lactose..." data-testid="quote-allergies" />
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[#2A2A28] font-medium text-sm">Notes ou demandes particulieres</Label>
              <Textarea className="mt-1.5 rounded-xl border-[#EBE8E0] min-h-[80px]" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Precisez vos besoins..." data-testid="quote-notes" />
            </div>
          </div>

          <Button type="submit" disabled={loading || settings?.quotes_enabled === false} className="w-full mt-6 rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white h-12 text-base font-medium" data-testid="quote-submit">
            {loading ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
          </Button>
        </form>
      </div>
    </PublicLayout>
  );
}
